import React, { useState } from 'react';
import { Form, Input, Button, message, Modal, ConfigProvider } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import s from './s.module.less';
import type { LoginFormData } from '../../types';
import { isValidEmail } from 'utils/form';
import {
    ERegisterType,
    sendForgetKlarity,
    registerAndLogin,
} from 'api/public';
import { PATH } from 'constants/path';
import { ERROR_CODE } from 'constants/common';
import { processAuthResponse } from 'utils/auth';
import { buildUtmObject } from 'utils/common';
import ProviderStore from 'store/Provider';
import { trackHeapEvent } from 'utils/analytics';
import { encryptString } from 'utils/encrypt';
import { setCurrentPlatformType } from 'utils/sessionStorage';

interface LoginFormProps {
    initialValues?: Partial<LoginFormData>;
    onSubmit?: (values: LoginFormData) => void;
    // 隐藏内置 "Log in" 标题（宿主页用自己的 provider-signup 风格标题时置 true）
    hideHeader?: boolean;
}

export default function LoginForm({ initialValues, onSubmit, hideHeader }: LoginFormProps) {
    const [form] = Form.useForm();
    const [forgotPasswordForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [forgotPasswordModalVisible, setForgotPasswordModalVisible] = useState(false);
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const navigate = useNavigate();
    const [hasSendEmail, setHasSendEmail] = useState(false);
    const { state } = useLocation();
    const [stateEmail] = useState(state?.email || '');
    const [, set] = ProviderStore.useStore();

    const handleSubmit = async (values: LoginFormData) => {
        try {
            setLoading(true);

            trackHeapEvent('WebApp - PPA Login - Click - Login Attempt', {
                em: await encryptString(values.email),
            });

            const result = await registerAndLogin({
                email: values.email,
                password: values.password,
                type: ERegisterType.KLARITY_PORTAL,
                utm: buildUtmObject(),
                signUpFrom: 'KLARITY_ONBOARDING',
                isLogin: true,
            });

            const authResult = processAuthResponse(result, set, ERegisterType.KLARITY_PORTAL);
            setCurrentPlatformType(ERegisterType.KLARITY_PORTAL);
            if (authResult) {
                // 登录成功埋点
                trackHeapEvent('WebApp - PPA Login - View - Login Success', {
                    em: await encryptString(values.email),
                });
                onSubmit?.(values);
            } else {
                // registerAndLogin 传了 disableErrorNote，后端文案不会自动弹出。多数失败确实是
                // 凭据问题，沿用原文案；但 STAFF_NO_PROVIDER_SCOPE 说明密码是对的、只是没有可
                // 落地的 provider，显示成密码错误会让用户和客服一直在重置密码上空转
                // （2026-08-07 org 6599 事故就是这么拖了三天）。原样透传后端 message。
                const isScopeError = (result as any)?.errorCode === ERROR_CODE.STAFF_NO_PROVIDER_SCOPE;
                const backendMessage = (result as any)?.error;
                // 登录失败埋点
                trackHeapEvent('WebApp - PPA Login - View - Login Failed', {
                    em: await encryptString(values.email),
                    reason: isScopeError ? 'staff_no_provider_scope' : 'invalid_credentials',
                });
                message.error(
                    isScopeError && backendMessage
                        ? backendMessage
                        : 'Invalid email or password. Please try again.',
                );
            }
        } catch (error) {
            // 登录错误埋点
            trackHeapEvent('WebApp - PPA Login - View - Login Error', {
                em: await encryptString(values.email),
                errorType: 'api_error',
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            message.error('Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        // 获取当前邮箱并预填到弹窗表单中
        const email = form.getFieldValue('email');
        if (email && isValidEmail(email)) {
            forgotPasswordForm.setFieldsValue({ email });
        } else {
            // 如果没有预填邮箱，清空表单
            forgotPasswordForm.resetFields();
        }

        // 忘记密码点击埋点
        trackHeapEvent('WebApp - PPA Login - Click - Forgot Password', {
            hasEmail: !!email,
            em: email ? await encryptString(email) : undefined,
        });

        setForgotPasswordModalVisible(true);
    };

    const handleCancel = () => {
        setForgotPasswordModalVisible(false);
        forgotPasswordForm.resetFields();
        setHasSendEmail(false);
    };

    const handleForgotPasswordSubmit = async (formValues: { email: string }) => {
        try {
            setForgotPasswordLoading(true);

            // 忘记密码提交埋点
            trackHeapEvent('WebApp - PPA Login - Click - Forgot Password Submit', {
                em: await encryptString(formValues.email),
            });

            const result = await sendForgetKlarity({
                email: formValues.email,
            });

            if (!result.error) {
                // 忘记密码成功埋点
                trackHeapEvent('WebApp - PPA Login - View - Forgot Password Success', {
                    em: await encryptString(formValues.email),
                });
                setHasSendEmail(true);
                // message.success('Password reset email sent successfully!');
                // setForgotPasswordModalVisible(false);
                // forgotPasswordForm.resetFields();
            } else {
                // 忘记密码失败埋点
                trackHeapEvent('WebApp - PPA Login - View - Forgot Password Failed', {
                    em: await encryptString(formValues.email),
                    reason: 'email_not_found',
                });
                message.error('We couldn\'t find the email you entered. Please double-check and try again.');
            }
        } catch (error) {
            // 忘记密码错误埋点
            trackHeapEvent('WebApp - PPA Login - View - Forgot Password Error', {
                em: await encryptString(formValues.email),
                errorType: 'api_error',
                error: error instanceof Error ? error.message : 'Unknown error',
            });
            message.error('Failed to send reset email. Please try again.');
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const handleForgotPasswordCancel = () => {
        setForgotPasswordModalVisible(false);
        forgotPasswordForm.resetFields();
    };

    const handleSignUp = () => {
        // 注册链接点击埋点
        trackHeapEvent('WebApp - PPA Login - Click - Sign Up Link', {
            from: 'login_page',
        });
        navigate(PATH.PPA_LEAD_FORM);
    };

    const handleSignUpFromForgotPassword = () => {
        // 忘记密码页面的注册链接点击埋点
        trackHeapEvent('WebApp - PPA Login - Click - Sign Up Link', {
            from: 'forgot_password_modal',
        });
        navigate(PATH.PPA_LEAD_FORM);
    };

    return (
        <div className={s.formContainer}>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: '#0E62F4',
                    },
                }}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    initialValues={{
                        email: stateEmail,
                        password: '',
                        ...initialValues,
                    }}
                    layout="vertical"
                    className={s.loginForm}
                >
                    {/* 表单头部（宿主页传 hideHeader 时隐藏，改用外层标题） */}
                    {!hideHeader && (
                        <div className={s.formHeader}>
                            <h2 className={s.formTitle}>Log in</h2>
                            <p className={s.formSubtitle}>
                                New to Klarity Health?{' '}
                                <button
                                    type="button"
                                    className={s.signUpLink}
                                    onClick={handleSignUp}
                                >
                                    Sign up
                                </button>
                            </p>
                        </div>
                    )}

                    {/* 表单主体 */}
                    <div className={s.formBody}>
                        {/* 邮箱输入 */}
                        <Form.Item
                            name="email"
                            label={
                                <span className={s.inputLabel}>
                                    Email address{' '}
                                    <span className={s.required}>*</span>
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your email',
                                },
                                {
                                    type: 'email',
                                    message: 'Please enter a valid email',
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder="Email address"
                                className={s.input}
                            />
                        </Form.Item>

                        {/* 密码输入 */}
                        <Form.Item
                            name="password"
                            label={
                                <span className={s.inputLabel}>
                                    Password{' '}
                                    <span className={s.required}>*</span>
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter your password',
                                },
                            ]}
                        >
                            <Input.Password
                                size="large"
                                placeholder="Password"
                                className={s.input}
                                iconRender={(visible) =>
                                (visible ? (
                                    <EyeTwoTone />
                                ) : (
                                    <EyeInvisibleOutlined />
                                ))
                                }
                            />
                        </Form.Item>

                        {/* 忘记密码链接 */}
                        <div className={s.forgotPassword}>
                            <button
                                type="button"
                                className={s.forgotPasswordLink}
                                onClick={handleForgotPassword}
                            >
                                Forgot password?
                            </button>
                        </div>
                    </div>

                    <div className={s.formActions}>
                        <Form.Item
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues.email !== currentValues.email ||
                                prevValues.password !== currentValues.password
                            }
                            style={{ marginBottom: 0 }}
                        >
                            {({ getFieldsValue }) => {
                                const { email, password } = getFieldsValue();
                                return (
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        loading={loading}
                                        disabled={!email || !password}
                                        className={s.submitButton}
                                        block
                                    >
                                        Continue
                                    </Button>
                                );
                            }}
                        </Form.Item>
                    </div>
                </Form>
            </ConfigProvider>

            <Modal
                title={null}
                open={forgotPasswordModalVisible}
                onCancel={handleForgotPasswordCancel}
                footer={null}
                centered
                className={s.forgotPasswordModal}
                destroyOnClose
            >
                <div className={s.modalContent}>
                    <div className={s.modalHeader}>
                        <h3 className={s.modalTitle}>Forgot password?</h3>
                        <p className={s.modalDescription}>
                            Enter your email address to reset password
                        </p>
                    </div>

                    <Form
                        form={forgotPasswordForm}
                        onFinish={handleForgotPasswordSubmit}
                        initialValues={{ email: '' }}
                        layout="vertical"
                        className={s.forgotPasswordForm}
                    >
                        <div className={s.inputContainer}>
                            <Form.Item
                                name="email"
                                label={
                                    <span className={s.modalInputLabel}>
                                        Email address{' '}
                                        <span className={s.required}>*</span>
                                    </span>
                                }
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please input your email!',
                                    },
                                    {
                                        type: 'email',
                                        message: 'Please input a valid email!',
                                    },
                                ]}
                                style={{ marginBottom: 0 }}
                            >
                                <Input
                                    size="large"
                                    placeholder=""
                                    className={s.modalInput}
                                />
                            </Form.Item>
                        </div>
                        {hasSendEmail && (
                            <div className={s.forgetTip}>
                                <div className={s.tipIcon} />
                                <div className={s.tipText}>
                                    If an account with this email exists, we&apos;ve
                                    sent password reset instructions. If you
                                    didn&apos;t receive an email, please{' '}
                                    <button
                                        type="button"
                                        className={s.signUpLinkInTip}
                                        onClick={handleSignUpFromForgotPassword}
                                    >
                                        sign up
                                    </button>
                                    .
                                </div>
                            </div>
                        )}
                        <div className={s.modalFooter}>
                            {!hasSendEmail ? (
                                <Form.Item
                                    shouldUpdate={(prevValues, currentValues) =>
                                        prevValues.email !== currentValues.email
                                    }
                                    style={{ marginBottom: 0 }}
                                >
                                    {({ getFieldValue }) => {
                                        const email = getFieldValue('email');
                                        return (
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                size="large"
                                                loading={forgotPasswordLoading}
                                                disabled={!email?.trim()}
                                                className={s.modalSubmitButton}
                                                block
                                            >
                                                Request a new password
                                            </Button>
                                        );
                                    }}
                                </Form.Item>
                            ) : (
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Button
                                        loading={forgotPasswordLoading}
                                        htmlType="submit"
                                        type="primary"
                                        size="large"
                                        className={s.modalSubmitButton}
                                        block
                                        onClick={async () => {
                                            // 重新发送邮件埋点
                                            const email =
                                                forgotPasswordForm.getFieldValue(
                                                    'email',
                                                );
                                            trackHeapEvent(
                                                'WebApp - PPA Login - Click - Resend Email',
                                                {
                                                    em: email
                                                        ? await encryptString(
                                                            email,
                                                        )
                                                        : undefined,
                                                },
                                            );
                                        }}
                                    >
                                        Resend
                                    </Button>
                                    <Button
                                        type="default"
                                        size="large"
                                        className={s.modalCancelButton}
                                        block
                                        onClick={() => {
                                            // 取消忘记密码埋点
                                            trackHeapEvent(
                                                'WebApp - PPA Login - Click - Forgot Password Cancel',
                                                {
                                                    hasSentEmail: hasSendEmail,
                                                },
                                            );
                                            handleCancel();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Form.Item>
                            )}

                            <p className={s.helpText}>
                                Need help? Contact us at{' '}
                                <a
                                    href="mailto:join@Helloklarity.com"
                                    className={s.contactLink}
                                >
                                    join@Helloklarity.com
                                </a>
                            </p>
                        </div>
                    </Form>
                </div>
            </Modal>
        </div>
    );
}
